// Super Admin API Service
// Base API URL - Use environment variable or proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem("token");
};

// Helper function to create headers with auth token
const getHeaders = () => ({
  "Content-Type": "application/json",
  "Authorization": `Bearer ${getAuthToken()}`,
});

// Payment method mapping helpers
// Map UI display values to API expected values
export const PAYMENT_METHOD_TO_API = {
  "Credit Card": "CreditCard",
  "Debit Card": "DebitCard",
  "Bank Transfer": "BankTransfer",
  "PayPal": "PayPal",
  "Stripe": "Stripe",
  "Manual": "Manual",
  "Cash": "Cash",
  "Check": "Check",
  "Wire Transfer": "WireTransfer",
  "Other": "Other"
};

// Map API values to UI display values
export const PAYMENT_METHOD_FROM_API = {
  "CreditCard": "Credit Card",
  "DebitCard": "Debit Card",
  "BankTransfer": "Bank Transfer",
  "PayPal": "PayPal",
  "Stripe": "Stripe",
  "Manual": "Manual",
  "Cash": "Cash",
  "Check": "Check",
  "WireTransfer": "Wire Transfer",
  "Other": "Other"
};

// ═══════════════════════════════════════════════════════════════════════════
// TENANT MANAGEMENT APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all tenants with optional filters
 * @param {Object} filters - { search, status, planId, onboarded, joinedFromUtc, joinedToUtc, page, pageSize }
 * @returns {Promise} - List of tenants
 */
export const getTenants = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("Search", filters.search);
  if (filters.status) params.append("Status", filters.status);
  if (filters.planId) params.append("PlanId", filters.planId);
  if (filters.onboarded !== undefined) params.append("Onboarded", filters.onboarded);
  if (filters.joinedFromUtc) params.append("JoinedFromUtc", filters.joinedFromUtc);
  if (filters.joinedToUtc) params.append("JoinedToUtc", filters.joinedToUtc);
  if (filters.page) params.append("Page", filters.page);
  if (filters.pageSize) params.append("PageSize", filters.pageSize);

  const url = `${API_BASE_URL}/api/admin/tenants${params.toString() ? `?${params.toString()}` : ""}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch tenants: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Get specific tenant by ID
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} - Tenant details
 */
export const getTenantById = async (tenantId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch tenant: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Update tenant status (Active, Suspended, Trial)
 * @param {string} tenantId - Tenant ID
 * @param {string} status - New status ("Active", "Suspended", "Pending")
 * @returns {Promise}
 */
export const updateTenantStatus = async (tenantId, status) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/status`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ status }),
  });

  if (!response.ok) throw new Error(`Failed to update tenant status: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Get tenant usage statistics
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} - Usage data (conversations, leads, agents, storage)
 */
export const getTenantUsage = async (tenantId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/usage`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch tenant usage: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Assign subscription plan to tenant
 * @param {string} tenantId - Tenant ID
 * @param {Object} subscriptionData - { subscriptionPlanId, startTrial, notes }
 * @returns {Promise}
 */
export const assignSubscription = async (tenantId, subscriptionData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/subscription`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(subscriptionData),
  });

  if (!response.ok) throw new Error(`Failed to assign subscription: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Cancel tenant subscription
 * @param {string} tenantId - Tenant ID
 * @param {Object} cancelData - { cancellationReason, endImmediately }
 * @returns {Promise}
 */
export const cancelSubscription = async (tenantId, cancelData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/subscription/cancel`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(cancelData),
  });

  if (!response.ok) throw new Error(`Failed to cancel subscription: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

// ═══════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION PLANS APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all subscription plans
 * @param {Object} filters - { search, isActive, isPublic }
 * @returns {Promise} - List of plans
 */
export const getPlans = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("Search", filters.search);
  if (filters.isActive !== undefined) params.append("IsActive", filters.isActive);
  if (filters.isPublic !== undefined) params.append("IsPublic", filters.isPublic);

  const url = `${API_BASE_URL}/api/admin/plans${params.toString() ? `?${params.toString()}` : ""}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch plans: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Create new subscription plan
 * @param {Object} planData - Plan details
 * @returns {Promise}
 */
export const createPlan = async (planData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/plans`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(planData),
  });

  if (!response.ok) throw new Error(`Failed to create plan: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Get specific plan by ID
 * @param {string} planId - Plan ID
 * @returns {Promise} - Plan details
 */
export const getPlanById = async (planId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/plans/${planId}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch plan: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Update subscription plan
 * @param {string} planId - Plan ID
 * @param {Object} planData - Updated plan details
 * @returns {Promise}
 */
export const updatePlan = async (planId, planData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/plans/${planId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(planData),
  });

  if (!response.ok) throw new Error(`Failed to update plan: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Delete subscription plan
 * @param {string} planId - Plan ID
 * @returns {Promise}
 */
export const deletePlan = async (planId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/plans/${planId}`, {
    method: "DELETE",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to delete plan: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

// ═══════════════════════════════════════════════════════════════════════════
// INVOICE MANAGEMENT APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all invoices with optional filters
 * @param {Object} filters - { search, status, tenantId, issueFromUtc, issueToUtc, minAmount, maxAmount, page, pageSize }
 * @returns {Promise} - List of invoices
 */
export const getInvoices = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("Search", filters.search);
  if (filters.status) params.append("Status", filters.status);
  if (filters.tenantId) params.append("TenantId", filters.tenantId);
  if (filters.issueFromUtc) params.append("IssueFromUtc", filters.issueFromUtc);
  if (filters.issueToUtc) params.append("IssueToUtc", filters.issueToUtc);
  if (filters.minAmount) params.append("MinAmount", filters.minAmount);
  if (filters.maxAmount) params.append("MaxAmount", filters.maxAmount);
  if (filters.page) params.append("Page", filters.page);
  if (filters.pageSize) params.append("PageSize", filters.pageSize);

  const url = `${API_BASE_URL}/api/admin/invoices${params.toString() ? `?${params.toString()}` : ""}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch invoices: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Create new invoice
 * @param {Object} invoiceData - Invoice details
 * @returns {Promise}
 */
export const createInvoice = async (invoiceData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invoices`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(invoiceData),
  });

  if (!response.ok) throw new Error(`Failed to create invoice: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Get specific invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise} - Invoice details
 */
export const getInvoiceById = async (invoiceId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch invoice: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Update invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} invoiceData - Updated invoice details
 * @returns {Promise}
 */
export const updateInvoice = async (invoiceId, invoiceData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(invoiceData),
  });

  if (!response.ok) throw new Error(`Failed to update invoice: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Issue an invoice
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise}
 */
export const issueInvoice = async (invoiceId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/issue`, {
    method: "POST",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to issue invoice: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Cancel an invoice
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise}
 */
export const cancelInvoice = async (invoiceId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/cancel`, {
    method: "POST",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to cancel invoice: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

/**
 * Record payment for invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} paymentData - { amount, method, paidAtUtc, externalPaymentId, receiptUrl, notes }
 * @returns {Promise}
 */
export const recordPayment = async (invoiceId, paymentData) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/payments`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(paymentData),
  });

  if (!response.ok) throw new Error(`Failed to record payment: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

// ═══════════════════════════════════════════════════════════════════════════
// PAYMENT MANAGEMENT APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all payments with optional filters
 * @param {Object} filters - { search, status, method, paidFromUtc, paidToUtc, page, pageSize }
 * @returns {Promise} - List of payments
 */
export const getPayments = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("Search", filters.search);
  if (filters.status) params.append("Status", filters.status);
  if (filters.method) params.append("Method", filters.method);
  if (filters.paidFromUtc) params.append("PaidFromUtc", filters.paidFromUtc);
  if (filters.paidToUtc) params.append("PaidToUtc", filters.paidToUtc);
  if (filters.page) params.append("Page", filters.page);
  if (filters.pageSize) params.append("PageSize", filters.pageSize);

  const url = `${API_BASE_URL}/api/admin/payments${params.toString() ? `?${params.toString()}` : ""}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch payments: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

// ═══════════════════════════════════════════════════════════════════════════
// AUDIT LOG APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get audit logs
 * @param {Object} filters - { search, action, actorUserId, targetTenantId, fromUtc, toUtc, page, pageSize }
 * @returns {Promise} - List of audit logs
 */
export const getAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("Search", filters.search);
  if (filters.action) params.append("Action", filters.action);
  if (filters.actorUserId) params.append("ActorUserId", filters.actorUserId);
  if (filters.targetTenantId) params.append("TargetTenantId", filters.targetTenantId);
  if (filters.fromUtc) params.append("FromUtc", filters.fromUtc);
  if (filters.toUtc) params.append("ToUtc", filters.toUtc);
  if (filters.page) params.append("Page", filters.page);
  if (filters.pageSize) params.append("PageSize", filters.pageSize);

  const url = `${API_BASE_URL}/api/admin/audit-log${params.toString() ? `?${params.toString()}` : ""}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch audit logs: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get dashboard statistics
 * Returns: totalTenants, activeTenants, pendingTenants, suspendedTenants,
 * revenueMtd, overdueInvoiceCount, activeSubscriptions, totalLeads,
 * totalConversations, recentTenants[], overdueInvoices[]
 * @returns {Promise} - Dashboard stats
 */
export const getDashboardStats = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch dashboard stats: ${response.statusText}`);
  const result = await response.json();
  // API returns ApiResponse<T> wrapper, extract data
  return result.data || result;
};

/**
 * Get tenants overview
 * Returns: totalTenants, activeTenants, pendingTenants, suspendedTenants,
 * totalLeads, totalConversations, tenants[]
 * @returns {Promise} - Tenants overview statistics
 */
export const getTenantsOverview = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/tenants/overview`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`Failed to fetch tenants overview: ${response.statusText}`);
  const result = await response.json();
  return result.data || result;
};

// Export all functions
export default {
  // Tenants
  getTenants,
  getTenantById,
  updateTenantStatus,
  getTenantUsage,
  assignSubscription,
  cancelSubscription,
  
  // Plans
  getPlans,
  createPlan,
  getPlanById,
  updatePlan,
  deletePlan,
  
  // Invoices
  getInvoices,
  createInvoice,
  getInvoiceById,
  updateInvoice,
  issueInvoice,
  cancelInvoice,
  recordPayment,
  
  // Payments
  getPayments,
  
  // Audit Logs
  getAuditLogs,
  
  // Dashboard
  getDashboardStats,
  getTenantsOverview,
};